import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { externalUserDashboardApi } from '../api/externalUserDashboardApi';
import { isCustomRangeIncomplete } from './usePeriodDates';
import type {
  ExternalUserDashboardData,
  ExternalDashboardFilters,
} from '../types/dashboard.types';

/**
 * Hook React Query pour récupérer les données du dashboard vendeur externe
 */
export const useExternalUserDashboard = (
  userId: string,
  filters?: ExternalDashboardFilters,
  warrantyName?: string,
  options?: UseQueryOptions<ExternalUserDashboardData>
) => {
  return useQuery({
    queryKey: ['external-user-dashboard', userId, filters, warrantyName],
    queryFn: () => externalUserDashboardApi.getDashboard(userId, filters, warrantyName),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Conserve les données de la période précédente pendant le chargement de la
    // nouvelle : sans ça, chaque changement de filtre repasse isLoading à true et
    // fait clignoter la page en spinner (et désactivait les champs de saisie).
    keepPreviousData: true,
    ...options,
    enabled: options?.enabled !== false && !!userId && !isCustomRangeIncomplete(filters ?? {}),
  });
};

/**
 * Hook React Query pour récupérer le dashboard d'une société (pour les admins)
 */
export const useSocietyDashboardV2 = (
  societyId: string,
  filters?: ExternalDashboardFilters,
  warrantyName?: string,
  options?: UseQueryOptions<ExternalUserDashboardData>
) => {
  return useQuery({
    queryKey: ['society-dashboard-v2', societyId, filters, warrantyName],
    queryFn: () => externalUserDashboardApi.getDashboardBySociety(societyId, filters, warrantyName),
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
    ...options,
    enabled: options?.enabled !== false && !!societyId && !isCustomRangeIncomplete(filters ?? {}),
  });
};
